// src/modules/PetSitter/components/ServiceSection.tsx
import { Box } from '@mui/material'
import { EmtIcon, EmtSelect, EmtTypography } from 'emt-components-v2'
import React from 'react'
import DateTimeForm from './DateTimeForm'
import DateTimeFormRecurrent from './DateTimeFormRecurrent'
import { FormSlot, ServiceSlug, serviceConfig, services, DateTimeInstance } from './requestConfig'

type Props = {
  isMobile: boolean
  selectedService: string | null
  errors: Record<string, string>
  formSlotsByService: Record<ServiceSlug, FormSlot[]>
  localStorageInitialValues: any
  formRefs: React.MutableRefObject<Record<string, any>>
  onServiceChange: (value: string) => void
  onAddNewSlot: () => void
  onSaveSlot: (slotId: string, instance: DateTimeInstance) => void
  onRemoveSlot: (slotId: string) => void
  showSelect?: boolean
  addButton?: boolean
  showGardeTimes?: boolean
  showTitle?: boolean
  showPrices?: boolean
}

export const ServiceSection: React.FC<Props> = ({
  isMobile,
  selectedService,
  errors,
  formSlotsByService,
  localStorageInitialValues,
  formRefs,
  onServiceChange,
  onAddNewSlot,
  onSaveSlot,
  onRemoveSlot,
  showSelect = false,
  addButton = true,
  showGardeTimes = true,
  showTitle = true,
  showPrices = false,
}) => {
  const slug = selectedService as ServiceSlug | null
  const isRecurrent = slug === 'promenade' || slug === 'visite'

  const renderServiceContent = () => {
    if (!slug) return null
    const config = serviceConfig[slug]
    const slots = formSlotsByService[slug] || []

    return (
      <Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {isRecurrent ? (
            (() => {
              const slot = formSlotsByService[slug]?.[0]
              const initial = slot?.data ?? (localStorageInitialValues || config.initialValues)
              return (
                <DateTimeFormRecurrent
                  key={slot?.id ?? 'slot-recurrent'}
                  ref={(ref: any) => {
                    if (slot) formRefs.current[slot.id] = ref
                  }}
                  onSave={(instance: DateTimeInstance) => slot && onSaveSlot(slot.id, instance)}
                  initialValues={initial}
                  // @ts-expect-error
                  formType={config.formType}
                />
              )
            })()
          ) : (
            <>
              {slots.map((slot) => {
                const initial = slot?.data ?? (localStorageInitialValues || config.initialValues)
                const CommonProps = {
                  ref: (ref: any) => (formRefs.current[slot.id] = ref),
                  onSave: (instance: DateTimeInstance) => onSaveSlot(slot.id, instance),
                  onCancel: () => onRemoveSlot(slot.id),
                  showCancel: slots.length > 1,
                  initialValues: initial,
                  formType: config.formType,
                  selectedService: slug,
                  showGardeTimes: showGardeTimes,
                }
                return <DateTimeForm key={slot.id} {...(CommonProps as any)} />
              })}
            </>
          )}
        </Box>

        {!isRecurrent && addButton && (
          <Box
            onClick={onAddNewSlot}
            sx={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginTop: '20px' }}
          >
            <Box
              sx={{
                backgroundColor: '#AE59C6',
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <EmtIcon className="emt-add-small" sx={{ fontSize: '17px', color: '#fff' }} />
            </Box>
            <EmtTypography variant="body2" fontWeight={700} color="#AE59C6" sx={{ textDecoration: 'underline' }}>
              {config.addButtonLabel}
            </EmtTypography>
          </Box>
        )}
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '15px' : '15px' }}>
      {/* Service navigation */}
      <Box sx={{ display: 'flex', alignItems: 'center', position: 'relative', zIndex: 0, flexWrap: 'wrap' }}>
        {isMobile || showSelect ? (
          <Box sx={{ width: '100%' }}>
            {!showSelect && showTitle && (
              <EmtTypography variant="body1" fontWeight="700" sx={{ marginBottom: '16px' }}>
                De quel service avez-vous besoin ?
              </EmtTypography>
            )}
            <Box
              sx={{
                backgroundColor: '#AE59C6!important',
                color: '#fff!important',
                '& .MuiSelect-select': {
                  padding: '10px!important',
                  backgroundColor: '#AE59C6!important',
                  color: '#fff!important',
                },
                '& *': {
                  color: '#fff!important',
                  backgroundColor: '#AE59C6!important',
                  borderColor: '#AE59C6!important',
                },
                maxHeight: '56px',
                height: '56px',
                borderRadius: '18px',
                overflow: 'hidden',
              }}
            >
              <EmtSelect
                options={services.map((s) => ({ label: s.title, value: s.slug }))}
                onChange={onServiceChange as any}
                error={!!errors.timeStart}
                helperText={errors.timeStart}
                placeholder="Sélectionner un service"
                iconClass="emt-caret-vertical-small-1"
                leftIconClassName={services.find((s) => s.slug === selectedService)?.iconClass || 'emt-paws'}
                value={selectedService || ''}
                sx={{
                  backgroundColor: '#AE59C6!important',
                  color: '#fff!important',
                  '& .MuiSelect-select': {
                    padding: '10px!important',
                    backgroundColor: '#AE59C6!important',
                    color: '#fff!important',
                  },
                  '& *': {
                    color: '#fff!important',
                    backgroundColor: '#AE59C6!important',
                    borderColor: '#AE59C6!important',
                  },
                  maxHeight: '56px',
                  height: '56px',
                }}
                // @ts-expect-error
                color="purple"
              />
            </Box>
          </Box>
        ) : (
          services.map((service) => {
            const isSelected = selectedService === service.slug

            return (
              <Box
                key={service.slug}
                component="button"
                type="button"
                aria-pressed={isSelected}
                sx={{
                  height: '48px',
                  backgroundColor: isSelected ? '#AE59C6' : '#F9F9F9',
                  color: isSelected ? '#fff' : '#1D1929',
                  borderRadius: '12px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '6px',
                  cursor: 'pointer',
                  flexGrow: 1,
                  padding: '0 10px',
                  border: 'none',
                  outline: 'none',
                  appearance: 'none',
                  font: 'inherit',

                  '&:focus-visible': {
                    outline: '2px solid #1D1929',
                    outlineOffset: '2px',
                  },
                }}
                onClick={() => onServiceChange(service.slug)}
              >
                {service.iconClass && <EmtIcon className={service.iconClass} aria-hidden="true" />}
                <EmtTypography variant="body2" fontWeight={700} fontSize="13px">
                  {service.title}
                </EmtTypography>
                {showPrices && (
                  <EmtTypography variant="body2" fontSize="12px" color="#7A7880">
                    {service.priceLabel}
                  </EmtTypography>
                )}
              </Box>
            )
          })
        )}
      </Box>

      {/* Content */}
      {(!isMobile || selectedService) && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? '20px' : '30px' }}>
          <Box>
            {slug && renderServiceContent()}
            {errors?.dateTimeInstances && (
              <EmtTypography variant="body2" fontSize="0.75rem" color="error" letterSpacing="0.03333em">
                {errors?.dateTimeInstances}
              </EmtTypography>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
